/**
 * @tt/shell-3d — instancing helpers
 *
 * One draw call for many copies of the same geometry — the lever for
 * keeping draw calls low when a game scatters dozens of coins, tiles,
 * rocks, projectiles, or crowd extras. Wraps THREE.InstancedMesh with an
 * ergonomic per-instance transform + color API and a `commit()` that flags
 * the GPU buffers in one shot.
 */
import * as THREE from 'three';

export interface InstancedFieldOptions {
  /** Cast shadows from every instance. Default true. */
  castShadow?: boolean;
  /** Receive shadows on every instance. Default false. */
  receiveShadow?: boolean;
  /** Enable per-instance color (adds an instanceColor buffer). Default true. */
  colors?: boolean;
}

export interface InstancedField {
  mesh: THREE.InstancedMesh;
  /** Max instances. */
  readonly capacity: number;
  /** Live instance count actually drawn (set via {@link setCount}). */
  count: number;
  /** Write one instance's transform. Marks the matrix buffer dirty. */
  setAt(
    i: number,
    pos: THREE.Vector3 | { x: number; y: number; z: number },
    quat?: THREE.Quaternion,
    scale?: THREE.Vector3 | number,
  ): void;
  /** Tint one instance (needs `colors: true`). */
  setColorAt(i: number, hex: number): void;
  /** Limit how many instances draw (the rest are skipped). */
  setCount(n: number): void;
  /** Push dirty buffers to the GPU — call once per frame after edits. */
  commit(): void;
  dispose(): void;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _c = new THREE.Color();

/**
 * Create an instanced field. The geometry + material are shared by all
 * instances; you own their disposal unless you pass freshly-made ones (the
 * field disposes the InstancedMesh's geometry/material on dispose()).
 */
export function createInstancedField(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  capacity: number,
  opts: InstancedFieldOptions = {},
): InstancedField {
  const { castShadow = true, receiveShadow = false, colors = true } = opts;
  const mesh = new THREE.InstancedMesh(geometry, material, capacity);
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  if (colors) {
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
  }
  mesh.count = capacity;

  return {
    mesh,
    capacity,
    count: capacity,
    setAt(i, pos, quat, scale) {
      _p.set(pos.x, pos.y, pos.z);
      _q.copy(quat ?? _q.identity());
      if (typeof scale === 'number') _s.setScalar(scale);
      else if (scale) _s.copy(scale);
      else _s.set(1, 1, 1);
      _m.compose(_p, _q, _s);
      mesh.setMatrixAt(i, _m);
      mesh.instanceMatrix.needsUpdate = true;
    },
    setColorAt(i, hex) {
      if (!mesh.instanceColor) return;
      mesh.setColorAt(i, _c.setHex(hex));
      mesh.instanceColor.needsUpdate = true;
    },
    setCount(n) {
      this.count = Math.max(0, Math.min(capacity, n));
      mesh.count = this.count;
    },
    commit() {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    dispose() {
      mesh.dispose();
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      mesh.removeFromParent();
    },
  };
}
